// Fabric can't serialize quadratic curves (https://stackoverflow.com/q/49047432/2255980), so instead
// of restoring the curve we restore its control points, re-bind their event listeners, and redraw.

import type { Canvas, StaticCanvas, Path } from 'fabric';
import getItemsByName from '@/utils/getItemsByName';
import calcArrowAngle from './calcArrowAngle';
import makePathLine from './makePathLine';
import makeCurveEnd from './makeCurveEnd';
import makeCurvePoint from './makeCurvePoint';
import makeArrowHead from './makeArrowHead';

// One control point of a serialized line (collected by importJSON's reviver).
export interface LinePoint {
  variation: string;
  top: number;
  left: number;
  stroke: string;
}

// A line = its 3 control points (start / curve / end). The pathLine + arrowHead are regenerated.
export interface LineGroup {
  pointStart: LinePoint;
  pointCurve: LinePoint;
  pointEnd: LinePoint;
}

const rebuildLines = (
  lineGroups: LineGroup[],
  canvas: Canvas | StaticCanvas,
  isMobile: boolean,
): void => {
  let itemCount = 1;
  // clear existing points before rebuilding
  for (const item of getItemsByName(canvas, 'pointStart')) canvas.remove(item);
  for (const item of getItemsByName(canvas, 'pointCurve')) canvas.remove(item);
  for (const item of getItemsByName(canvas, 'pointEnd')) canvas.remove(item);

  // `lineGroups` is 1-indexed (importJSON fills from lineId=1) so index 0 is a hole; forEach skips
  // holes, whereas a for-of would iterate the hole as `undefined` and crash on `lineGroup.pointStart`.
  // eslint-disable-next-line unicorn/no-for-each -- sparse array (see above)
  lineGroups.forEach((lineGroup) => {
    // top+left of the 3 path coordinates (start, curve, end)
    const path01 = lineGroup.pointStart.left;
    const path02 = lineGroup.pointStart.top;
    const path11 = lineGroup.pointCurve.left;
    const path12 = lineGroup.pointCurve.top;
    const path13 = lineGroup.pointEnd.left;
    const path14 = lineGroup.pointEnd.top;

    const angle = calcArrowAngle(path11, path12, path13, path14);
    const arrowHead = makeArrowHead(path13, path14, angle);

    const pointStart = makeCurveEnd(path01, path02);
    const pointCurve = makeCurvePoint(path11, path12, isMobile);
    const pointEnd = makeCurveEnd(path13, path14);

    // ids are used to keep track of which point coordinates belong to which pathLine
    pointStart.id = itemCount;
    pointCurve.id = itemCount;
    pointEnd.id = itemCount;
    itemCount++;

    // naming each point helps us identify it later
    pointStart.name = 'pointStart';
    pointCurve.name = 'pointCurve';
    pointEnd.name = 'pointEnd';

    pointStart.variation = lineGroup.pointStart.variation;
    pointCurve.variation = lineGroup.pointCurve.variation;
    pointEnd.variation = lineGroup.pointEnd.variation;

    const color = lineGroup.pointStart.stroke;
    const pathLine = makePathLine(lineGroup.pointStart.variation);

    pathLine.pointStart = pointStart;
    pathLine.arrowHead = arrowHead;
    pathLine.pointCurve = pointCurve;
    pathLine.pointEnd = pointEnd;

    pointStart.pathLine = pathLine;
    pointStart.arrowHead = arrowHead;
    pointStart.pointCurve = pointCurve;
    pointStart.pointEnd = pointEnd;

    pointCurve.pathLine = pathLine;
    pointCurve.arrowHead = arrowHead;
    pointCurve.pointStart = pointStart;
    pointCurve.pointEnd = pointEnd;

    pointEnd.pathLine = pathLine;
    pointEnd.arrowHead = arrowHead;
    pointEnd.pointStart = pointStart;
    pointEnd.pointCurve = pointCurve;

    arrowHead.set({
      fill: color,
      stroke: color,
    });

    arrowHead.set({
      fill: color,
      stroke: color,
    });

    pointStart.set({
      stroke: color,
    });

    pointCurve.set({
      stroke: color,
    });

    pointEnd.set({
      stroke: color,
    });

    pathLine.set({
      left: 0,
      path: [
        ['M', path01, path02],
        ['Q', path11, path12, path13, path14],
      ] as unknown as Path['path'],
      stroke: lineGroup.pointCurve.stroke,
    });

    // keep this add order — lines render incorrectly otherwise
    canvas.add(pathLine);
    canvas.add(arrowHead);
    canvas.add(pointCurve);
    canvas.add(pointStart);
    canvas.add(pointEnd);
  });
};

export default rebuildLines;
